import React from 'react'
import { useTranslation } from 'react-i18next'

const TranslationsLoading = () => null

/**
 * Custom react-i18next withTranslationCustom HoC to render null when loading not ready.
 *
 * Why?:
 * This mimics the old behaviour (before react-i18next introduced "suspense" in v10),
 * and requires no changes from our end.
 * The wrapped component will not render anything until all required files are loaded or fails to load.
 *
 * This could be improved by providing optional argument for loading indicator component etc,
 * but as the language file loads are usually super fast, it's not really something that is required at this time.
 */
export function withTranslationCustom (ns) {
    const options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {}
    return function Extend (WrappedComponent) {
        function I18NextWithTranslation (_ref) {
            const { forwardedRef, ...rest } = _ref

            const { t, i18n, ready } = useTranslation(ns, rest)

            const passDownProps = {
                ...rest,
                t,
                i18n,
                tReady: ready,
            }

            if (options.withRef && forwardedRef) {
                passDownProps.ref = forwardedRef
            } else if (!options.withRef && forwardedRef) {
                passDownProps.forwardedRef = forwardedRef
            }

            if (!ready) return React.createElement(TranslationsLoading)

            return React.createElement(WrappedComponent, passDownProps)
        }

        const forwardRef = function forwardRef (props, ref) {
            return React.createElement(I18NextWithTranslation, Object.assign({}, props, {
                forwardedRef: ref
            }))
        }

        return options.withRef ? React.forwardRef(forwardRef) : I18NextWithTranslation
    }
}
